/**
 * 1:1 chat — Firestore `chats/{chatId}/messages`.
 * Chat id is deterministic (sorted uids joined by `_`).
 */

import {
  collection,
  doc,
  getDoc,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { USER_NOTIFICATIONS_COLLECTION } from '../users/userNotifications';
import { getFriendsProfiles, getUserProfile, USERS_COLLECTION } from '../users/userProfile';
import { awardMapHas, getEarnCoins } from '../users/earnCatalog';

/** Firestore collection name — wire value unchanged. */
export const CHATS_COLLECTION = 'chats';
/** Firestore subcollection name — wire value unchanged. */
export const CHAT_MESSAGES_SUBCOLLECTION = 'messages';
export const CHAT_MESSAGE_MAX = 1000;
export const CHAT_LAST_MESSAGE_MAX = 120;
export const CHAT_INBOX_LIMIT = 50;
export const CHAT_THREAD_LIMIT = 50;
/** Flip to false (with the rules comment) to require friendship. */
export const CHAT_ALLOW_NON_FRIENDS = true;

export function chatMessageNotifId(chatId, toUid) {
  return `chat_${chatId}_${toUid}`;
}

export function dmChatId(uidA, uidB) {
  const a = String(uidA || '').trim();
  const b = String(uidB || '').trim();
  if (!a || !b) throw new Error('Need two people to start a chat.');
  if (a === b) throw new Error('You cannot message yourself.');
  return [a, b].sort().join('_');
}

/** Note-to-yourself thread. Id matches two equal members (`uid_uid`). */
export function selfChatId(uid) {
  const me = String(uid || '').trim();
  if (!me) throw new Error('Need a person to start a note.');
  return `${me}_${me}`;
}

export function isSelfChat(chatOrId, uid) {
  const me = String(uid || '').trim();
  if (!me) return false;
  if (chatOrId && typeof chatOrId === 'object') {
    const members = Array.isArray(chatOrId.memberUids)
      ? chatOrId.memberUids.filter(Boolean)
      : [];
    return members.length > 0 && members.every((id) => id === me);
  }
  return String(chatOrId || '') === selfChatId(me);
}

export function chatPartnerUid(chatOrId, uid) {
  const me = String(uid || '').trim();
  if (!me) return '';
  if (chatOrId && typeof chatOrId === 'object') {
    const members = Array.isArray(chatOrId.memberUids)
      ? chatOrId.memberUids.filter(Boolean)
      : [];
    const other = members.find((id) => id !== me);
    if (other) return other;
    if (members.length > 0 && members.every((id) => id === me)) return me;
    return '';
  }
  const chatId = String(chatOrId || '');
  const prefix = `${me}_`;
  const suffix = `_${me}`;
  if (chatId.startsWith(prefix)) return chatId.slice(prefix.length);
  if (chatId.endsWith(suffix)) return chatId.slice(0, -suffix.length);
  return '';
}

function clipText(value, max) {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.length > max ? text.slice(0, max) : text;
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const d = value instanceof Date ? value : new Date(value);
  const n = d.getTime();
  return Number.isFinite(n) ? n : 0;
}

export function normalizeChatDoc(id, data = {}) {
  const last = data.lastMessage && typeof data.lastMessage === 'object' ? data.lastMessage : null;
  return {
    id: id || '',
    memberUids: Array.isArray(data.memberUids) ? data.memberUids.filter(Boolean) : [],
    createdBy: data.createdBy || '',
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
    lastMessage: last
      ? {
          text: typeof last.text === 'string' ? last.text : '',
          senderUid: last.senderUid || '',
          createdAt: last.createdAt || null,
        }
      : null,
    unread: data.unread && typeof data.unread === 'object' ? data.unread : {},
    lastReadAt: data.lastReadAt && typeof data.lastReadAt === 'object' ? data.lastReadAt : {},
  };
}

export function normalizeChatMessageDoc(id, data = {}) {
  return {
    id: id || '',
    senderUid: data.senderUid || '',
    text: typeof data.text === 'string' ? data.text : '',
    type: data.type === 'text' ? 'text' : 'text',
    createdAt: data.createdAt || null,
  };
}

export function unreadCountFor(chat, uid) {
  if (!uid || !chat) return 0;
  const n = Number(chat.unread?.[uid]);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
}

export function sortChatsByUpdated(chats = []) {
  return [...(chats || [])].sort((a, b) => toMillis(b?.updatedAt) - toMillis(a?.updatedAt));
}

export function subscribeUserChats(uid, onChange, onError) {
  if (!uid) {
    onChange([]);
    return () => {};
  }
  const q = query(
    collection(db, CHATS_COLLECTION),
    where('memberUids', 'array-contains', uid),
    orderBy('updatedAt', 'desc'),
    limit(CHAT_INBOX_LIMIT)
  );
  return onSnapshot(
    q,
    (snap) => {
      const rows = (snap.docs || []).map((d) => normalizeChatDoc(d.id, d.data()));
      onChange(sortChatsByUpdated(rows));
    },
    (err) => {
      if (onError) onError(err);
      else console.error('subscribeUserChats failed:', err);
    }
  );
}

export function subscribeChatMessages(chatId, onChange, onError, { max = CHAT_THREAD_LIMIT } = {}) {
  if (!chatId) {
    onChange([]);
    return () => {};
  }
  const cap = Math.max(1, Math.min(Number(max) || CHAT_THREAD_LIMIT, 200));
  const q = query(
    collection(db, CHATS_COLLECTION, chatId, CHAT_MESSAGES_SUBCOLLECTION),
    orderBy('createdAt', 'desc'),
    limit(cap)
  );
  return onSnapshot(
    q,
    (snap) => {
      const newestFirst = (snap.docs || []).map((d) =>
        normalizeChatMessageDoc(d.id, d.data())
      );
      onChange([...newestFirst].reverse());
    },
    (err) => {
      if (onError) onError(err);
      else console.error('subscribeChatMessages failed:', err);
    }
  );
}

async function assertCanMessage(fromUid, toUid) {
  if (CHAT_ALLOW_NON_FRIENDS) return;
  const from = await getUserProfile(fromUid);
  const ids = Array.isArray(from?.friendIds) ? from.friendIds : [];
  if (!ids.includes(toUid)) {
    throw new Error('You can only message friends.');
  }
}

export async function sendChatMessage({ chatId, fromUid, toUid, text }) {
  const from = String(fromUid || '').trim();
  const to = String(toUid || '').trim();
  if (!from || !to) throw new Error('Need a sender and a recipient.');
  const toSelf = from === to;
  const expectedId = toSelf ? selfChatId(from) : dmChatId(from, to);
  const id = String(chatId || expectedId).trim() || expectedId;
  if (id !== expectedId) throw new Error('Chat does not match those people.');

  const body = String(text || '').trim();
  if (!body) throw new Error('Write a message first.');
  if (body.length > CHAT_MESSAGE_MAX) {
    throw new Error(`Messages can be ${CHAT_MESSAGE_MAX} characters.`);
  }

  if (!toSelf) await assertCanMessage(from, to);

  const fromProfile = toSelf ? null : await getUserProfile(from);
  const friendIds = Array.isArray(fromProfile?.friendIds) ? fromProfile.friendIds : [];
  const alreadyMessaged = awardMapHas(fromProfile?.mCoinAwards?.firstMessages, to);
  const qualifies = !toSelf && friendIds.includes(to) && !alreadyMessaged;
  const earned = qualifies ? getEarnCoins().FIRST_MESSAGE : 0;

  const preview = clipText(body, CHAT_LAST_MESSAGE_MAX);
  const chatRef = doc(db, CHATS_COLLECTION, id);
  const messageRef = doc(collection(db, CHATS_COLLECTION, id, CHAT_MESSAGES_SUBCOLLECTION));
  const snap = await getDoc(chatRef);
  const batch = writeBatch(db);
  const lastMessage = {
    text: preview,
    senderUid: from,
    createdAt: serverTimestamp(),
  };

  if (!snap.exists()) {
    batch.set(chatRef, {
      memberUids: toSelf ? [from, from] : [from, to].sort(),
      createdBy: from,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastMessage,
      unread: toSelf
        ? { [from]: 0 }
        : {
            [from]: 0,
            [to]: 1,
          },
      lastReadAt: {
        [from]: serverTimestamp(),
      },
    });
  } else {
    const patch = {
      lastMessage,
      updatedAt: serverTimestamp(),
    };
    if (!toSelf) patch[`unread.${to}`] = increment(1);
    batch.update(chatRef, patch);
  }

  batch.set(messageRef, {
    senderUid: from,
    text: body,
    type: 'text',
    createdAt: serverTimestamp(),
  });
  if (!toSelf) {
    batch.set(
      doc(db, USER_NOTIFICATIONS_COLLECTION, chatMessageNotifId(id, to)),
      {
        type: 'chat_message',
        fromUid: from,
        toUid: to,
        chatId: id,
        preview,
        messageId: messageRef.id,
        status: 'unread',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }
  if (qualifies) {
    const userPatch = {
      [`mCoinAwards.firstMessages.${to}`]: earned,
      updatedAt: serverTimestamp(),
    };
    if (earned > 0) userPatch.mCoins = increment(earned);
    batch.update(doc(db, USERS_COLLECTION, from), userPatch);
  }
  await batch.commit();
  return { chatId: id, messageId: messageRef.id, earned };
}

export async function markChatRead(chatId, uid) {
  const id = String(chatId || '').trim();
  const me = String(uid || '').trim();
  if (!id || !me) return;
  const chatRef = doc(db, CHATS_COLLECTION, id);
  const snap = await getDoc(chatRef);
  if (!snap.exists()) return;
  await updateDoc(chatRef, {
    [`unread.${me}`]: 0,
    [`lastReadAt.${me}`]: serverTimestamp(),
  });
}

export async function hydrateChatPartners(chats = [], uid) {
  const partnerUids = [
    ...new Set((chats || []).map((chat) => chatPartnerUid(chat, uid)).filter(Boolean)),
  ];
  const profiles = await getFriendsProfiles(partnerUids);
  const map = {};
  (profiles || []).forEach((p) => {
    if (p?.id) map[p.id] = p;
  });
  return map;
}
